/**
 * A studio config, read structurally (PLAN §7.1a, D22, D32): devices and
 * where they connect, the mappings from show-style layers to them, and the
 * take span. The mapping table judges those mappings against a show style;
 * the JSON editor is where the document changes.
 */
import type { ReactNode } from 'react';
import type { StudioSummaryView } from '../../lib/studio/config.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type StudioConfigSummaryProps = { readonly summary: StudioSummaryView };

function Section({ title, count, children }: { readonly title: string; readonly count: number; readonly children: ReactNode }) {
  return (
    <section className="grid gap-2" aria-label={title}>
      <h3 className="text-sm font-semibold">
        {title} <span className="font-normal text-muted-foreground">({count})</span>
      </h3>
      {count === 0 ? <p className="text-sm text-muted-foreground">None.</p> : children}
    </section>
  );
}

export function StudioConfigSummary({ summary }: StudioConfigSummaryProps) {
  return (
    <div className="grid gap-6" data-testid="studio-config-summary">
      <Section title="Devices" count={summary.devices.length}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Id</TableHead>
              <TableHead>Driver</TableHead>
              <TableHead>Connection</TableHead>
              <TableHead>Standby</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.devices.map((d) => (
              <TableRow key={d.id}>
                <TableCell className="font-mono">{d.id}</TableCell>
                <TableCell>{d.type}</TableCell>
                <TableCell className="font-mono text-xs">{d.connection}</TableCell>
                <TableCell>{d.standbyPolicy}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      <Section title="Mappings" count={summary.mappings.length}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Layer</TableHead>
              <TableHead>Device</TableHead>
              <TableHead>Lookahead</TableHead>
              <TableHead>One-shot</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.mappings.map((m) => (
              <TableRow key={m.layer}>
                <TableCell className="font-mono">{m.layer}</TableCell>
                <TableCell className="font-mono">{m.device}</TableCell>
                <TableCell>{m.lookahead}</TableCell>
                <TableCell>{m.oneShot ? 'yes' : 'no'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      <p className="text-sm text-muted-foreground">
        Minimum take span: <span className="font-mono">{summary.minimumTakeSpanMs} ms</span>
      </p>
    </div>
  );
}
